import React from 'react';
import type { UploadChangeParam } from 'antd/es/upload';
import { Button, Spin, Upload, message } from 'antd';
import type { RcFile, UploadFile, UploadProps } from 'antd/es/upload/interface';
import { uploadFile } from 'api/common';
import UploadIcon from './upload.svg';
import { getFileNameFromUrl } from 'utils/common';
import PdfIcon from './pdf.png';
import s from './s.module.less';

const { Dragger } = Upload;

type FileUploadProps = {
    styles?: React.CSSProperties;
    value?: string;
    tips?: string;
    onChange?: (val: string) => void;
    accept?: string;
    maxSize?: number;
    acceptTypeStr?: string;
    showImage?: boolean;
};

const FileUpload = (Props: FileUploadProps) => {
    const { onChange, accept, value, tips, styles, maxSize = 10, acceptTypeStr, showImage } = Props;
    const [loading, setLoading] = React.useState(false);
    const [imageUrl, setImageUrl] = React.useState<string>(UploadIcon);

    const fileInfoRef = React.useRef<UploadChangeParam<UploadFile>>();
    const [showUploadList, setShowUploadList] = React.useState(false);
    const displayInitImgName = !!value;

    const getBase64 = (img: RcFile, callback: (url: string) => void) => {
        const reader = new FileReader();
        reader.addEventListener('load', () => callback(reader.result as string));
        reader.readAsDataURL(img);
    };

    const handleChange: UploadProps['onChange'] = (info: UploadChangeParam<UploadFile>) => {
        fileInfoRef.current = info;
        if (info.file.status === 'uploading') {
            setLoading(true);
        }
    };
    const props: UploadProps = {
        name: 'file',
        multiple: false,
        onChange: handleChange,
        accept: accept || '*',
        showUploadList,
        beforeUpload(file) {
            return new Promise((resolve, reject) => {
                const isLtSize = file.size / 1024 / 1024 <= maxSize;
                if (!isLtSize) {
                    message.error(`File must be smaller than ${maxSize}MB!`);
                    reject(false);
                } else {
                    resolve(true);
                }
            });
        },
        onDrop() {
            //console.log('Dropped files', e.dataTransfer.files);
        },
        customRequest: async (data) => {
            //https://github.com/react-component/upload#customrequest
            try {
                const formData = new FormData();
                formData.append('file', data.file);
                console.log(data.file);
                const res = await uploadFile(formData);
                if (!res.error) {
                    if (fileInfoRef.current) {
                        fileInfoRef.current.file.status = 'done';
                    }
                    setShowUploadList(true);
                    const url = res.data?.data;
                    const isPdf = fileInfoRef.current?.file?.name
                        ?.toLowerCase()
                        .endsWith('pdf');
                    if (isPdf) {
                        setImageUrl(PdfIcon);
                    } else {
                        getBase64(data.file as RcFile, (base64Url) => {
                            setImageUrl(base64Url);
                        });
                    }
                    onChange?.(url);
                }
            } catch (e) {
                setShowUploadList(false);
                console.error(e);
            } finally {
                setLoading(false);
            }
        },
    };
    return (
        <div className={s.wrap} style={styles}>
            <Spin spinning={loading}>
                {!value && (
                    <Dragger {...props}>
                        <div style={styles}>
                            <p className={s.iconWrap}>
                                <img src={UploadIcon} className={s.icon} />
                            </p>
                            <p className={s.uploadText}>
                                <span>Upload a file</span> or drag and drop
                            </p>
                            <p className={s.uploadHint}>{tips ?? `${acceptTypeStr || 'PNG, JPG, PDF'} up to ${maxSize}MB`}</p>
                        </div>
                    </Dragger>
                )}
            </Spin>
            {displayInitImgName && !showImage && (
                <div className={s.uploadName}>
                    <span>{getFileNameFromUrl(value)}</span>
                    <span
                        onClick={() => {
                            onChange?.('');
                        }}
                        className={s.remove}
                    >
                        Remove
                    </span>
                </div>
            )}
            {
                displayInitImgName && showImage && (
                    <div className={s.uploadImageWrap}>
                        <img className={s.previewImage} src={value || imageUrl} alt="upload" />
                        <span
                            onClick={() => {
                                onChange?.('');
                            }}
                            className={s.remove}
                        >
                            Remove
                        </span>

                    </div>
                )
            }
        </div>
    );
};

export default FileUpload;
